'use client';

import styles from './QuestionnaireNavigation.module.css';
import type { QuestionnaireNavigationProps } from './QuestionnaireNavigation.types';
import { Shimmer, Heading, Flex } from '@/_components';
import { QuestionnaireNavigationItem } from './components/QuestionnaireNavigationItem';
import { filterUploadOnlySections, getSectionStatus } from '@/_utils/sections';
import { useContext, type FC } from 'react';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import type { Section } from '@/_types/models/sections';
import { QuestionnaireNavigationLoading } from './components/QuestionnaireNavigationLoading';

export const QuestionnaireNavigation: FC<QuestionnaireNavigationProps> = ({
  isLoading,
  mode,
  questions,
  sections,
}) => {
  const { preferences } = useContext(FirmSettingsContext);

  const filteredSections: Array<Section> = filterUploadOnlySections(
    sections,
    !!preferences?.hide_document_uploads_within_sections,
  );

  const sectionCount: number = filteredSections?.length ?? 0;
  const completedSectionCount: number =
    filteredSections
      ?.map((s) => {
        const status = getSectionStatus(questions, s);

        if (status === 'completed') return true;
        return false;
      })
      .filter(Boolean)?.length ?? 0;

  return (
    <nav
      className={styles.questionnaireNavigationContainer}
      data-testid="QuestionnaireNavigation-sidebar"
    >
      <Flex className={styles.questionnaireNavigationCount} isCentered={true}>
        <Heading
          level={5}
          className={styles.questionnaireNavigationHeaderNormal}
        >
          {isLoading ? (
            <Shimmer height={24} width={80} />
          ) : (
            `${completedSectionCount} / ${sectionCount}`
          )}
        </Heading>
        <Heading
          level={7}
          className={styles.questionnaireNavigationHeaderSmall}
        >
          {isLoading ? (
            <Shimmer height={24} width={36} />
          ) : (
            `${completedSectionCount} / ${sectionCount}`
          )}
        </Heading>
      </Flex>
      {isLoading || !filteredSections?.length ? (
        <QuestionnaireNavigationLoading />
      ) : (
        <Flex
          isColumn={true}
          padding={2}
          gap={2}
          className={styles.questionnaireNavigation}
        >
          {filteredSections.map((section, key) => {
            return (
              <QuestionnaireNavigationItem
                mode={mode}
                section={section}
                key={key}
              />
            );
          })}
        </Flex>
      )}
    </nav>
  );
};
